import { Row, Space, Spin } from 'antd'
import {
  FieldDesignChangeStatus,
  alphabeticalCompare,
  alphabeticalSort,
} from '@cims-online/shared'
import { useEffect, useState } from 'react'
import { FieldDesignChange } from '../../../../../../FDCTrackerContext/types'
import ConcurrenceLevelFormItem from '../../../../components/ConcurrenceLevelFormItem'
import { CheckableGroupItem } from '../../types'
import CIMSFormItem from 'components/CIMSFormItem'
import { CIMSModal } from 'components/CIMSModal'
import { useCIMSModal } from 'components/CIMSModal/hooks/useCIMSModal'
import { CIMSTag } from 'components/CIMSTag'
import CIMSComment from 'components/CIMSComment'
import { useFDCTrackerContext } from '../../../../../../FDCTrackerContext'
import { toConcurrenceLevelValue } from '../../../../components/ConcurrenceLevelFormItem/utils/toConcurrenceLevelValue'
import CIMSCheckbox from 'components/CIMSCheckbox'
import ActionButton from './components/ActionButton'
import { useGenericComment } from 'components/CIMSComment/hooks/useGenericComment'

const ConcurrenceGroups: React.FC<FieldDesignChange> = ({
  id,
  statusId,
  concurrenceLevel,
  fdcToFDCConcurrenceGroups,
  deleted,
}) => {
  const {
    metadata,
    mutations: { concur },
  } = useFDCTrackerContext()
  const [visible, toggle, state] = useCIMSModal<{
    concurrenceGroupId: string
  }>()
  const [groups, setGroups] = useState<CheckableGroupItem[]>([])
  useEffect(() => {
    if (
      FieldDesignChangeStatus.fromIdOrFail(statusId).priority >=
      FieldDesignChangeStatus.Closed.priority
    )
      setGroups(
        fdcToFDCConcurrenceGroups
          .sort((a, b) =>
            alphabeticalCompare(a.concurrenceGroup, b.concurrenceGroup, 'name')
          )
          .reduce<CheckableGroupItem[]>(
            (
              items,
              {
                concurred,
                concurrenceGroup: { id, name, description, deleted },
                concurrenceBy: { displayName, mail },
              }
            ) => {
              if (!deleted)
                items.push({
                  id,
                  name,
                  description,
                  checked: concurred,
                  by: displayName ?? mail,
                })
              return items
            },
            []
          )
      )
    else
      setGroups(
        (metadata?.fdcConcurrenceGroups ?? [])
          .sort(alphabeticalSort('name'))
          .map(({ id, name, description }) => {
            const fdc2group = fdcToFDCConcurrenceGroups.find(
              ({ concurrenceGroup }) => concurrenceGroup.id === id
            )
            const checked = fdc2group?.concurred ?? false
            const by =
              fdc2group?.concurrenceBy?.displayName ??
              fdc2group?.concurrenceBy?.mail
            return { id, name, description, checked, by }
          })
      )
    return () => setGroups([])
  }, [metadata?.fdcConcurrenceGroups, fdcToFDCConcurrenceGroups, statusId])
  const commentProps = useGenericComment({ required: { action: 'rejection' } })
  if (
    FieldDesignChangeStatus.fromIdOrFail(statusId).priority ===
    FieldDesignChangeStatus.Draft.priority
  )
    return null
  const level = concurrenceLevel?.description
    ? `${concurrenceLevel?.name} - ${concurrenceLevel?.description}`
    : concurrenceLevel?.name
  const key = (concurrenceGroupId: string) => `${concurrenceGroupId}-${id}`
  return (
    <Spin spinning={metadata?.loading}>
      <CIMSModal
        key={`reject-concurrence-group-${id}`}
        title={`Reject ${level}`}
        visible={visible}
        onCancel={() => toggle(null)}
        initialFormValues={{
          concurrenceLevel: toConcurrenceLevelValue(concurrenceLevel),
          comment: undefined,
        }}
        onOk={({ comment, concurrenceLevel }) => {
          if (!state) return
          concur.fn(key(state.concurrenceGroupId), {
            input: {
              concurred: false,
              concurrenceGroupId: state.concurrenceGroupId,
              fieldDesignChangeId: id,
              comment,
              concurrenceLevelId: concurrenceLevel?.value as string,
            },
          })
          toggle(null)
        }}
        narrow
        loading={concur.is(id)}
      >
        <ConcurrenceLevelFormItem required />
        <CIMSFormItem {...commentProps}>
          <CIMSComment />
        </CIMSFormItem>
      </CIMSModal>
      {groups.map(
        ({ name, description, id: concurrenceGroupId, checked, by }) => {
          const can = concur.can.get(concurrenceGroupId) && !checked
          const loading = concur.is(key(concurrenceGroupId))
          return (
            <Row
              key={`concurrence-groups-${id}-${concurrenceGroupId}`}
              align="middle"
              justify="space-between"
            >
              <CIMSTag
                tooltip={description}
                style={{ maxWidth: can ? 100 : 120 }}
              >
                {name}
              </CIMSTag>
              {can && !deleted ? (
                <Spin spinning={loading}>
                  <Space size="small">
                    <ActionButton
                      disabled={loading}
                      onClick={() =>
                        concur.fn(key(concurrenceGroupId), {
                          input: {
                            concurred: true,
                            concurrenceGroupId,
                            fieldDesignChangeId: id,
                          },
                        })
                      }
                    >
                      Concur
                    </ActionButton>
                    <ActionButton
                      disabled={loading}
                      onClick={() => toggle({ concurrenceGroupId })}
                    >
                      Reject
                    </ActionButton>
                  </Space>
                </Spin>
              ) : (
                <CIMSCheckbox
                  style={{ padding: '1px 0px' }}
                  checked={checked}
                  loading={loading}
                  disabled={[
                    {
                      disabled:
                        statusId ===
                        FieldDesignChangeStatus.PendingApprovals.id,
                      reason: {
                        why: 'invalid status',
                        statusName:
                          FieldDesignChangeStatus.PendingApprovals.name,
                      },
                    },
                    {
                      disabled:
                        statusId === FieldDesignChangeStatus.Unlocked.id,
                      reason: {
                        why: 'invalid status',
                        statusName: FieldDesignChangeStatus.Unlocked.name,
                      },
                    },
                    {
                      disabled: statusId === FieldDesignChangeStatus.Closed.id,
                      reason: {
                        why: 'invalid status',
                        statusName: FieldDesignChangeStatus.Closed.name,
                      },
                    },
                    {
                      disabled: !can && !concur.can.get(concurrenceGroupId),
                      reason: 'missing permission',
                    },
                    {
                      disabled: !can && checked,
                      reason: {
                        why: 'disabled',
                        message: `Already concurred by ${by}`,
                      },
                    },
                    {
                      disabled: deleted,
                      reason: { why: 'inactive', entityName: 'FDC' },
                    },
                  ]}
                />
              )}
            </Row>
          )
        }
      )}
    </Spin>
  )
}

export default ConcurrenceGroups
